import React, { useEffect, useState } from "react";
import Sidebar from "../side_bar/SidebarAdmin";
import avatar from "../../../assets/avatar.png";
import { useAuth } from "../../../context/AuthContext";
import {
  getDashboardSummary,
  DashboardSummary,
  getSpeciesDistribution,
  getRegionsDistribution,
  getCitiesDistribution,
  DistributionResponse,
} from "../../../api/admin/dashboard";
import "./DashboardAdmin.css";
import Header from "../../../components/common/Header";
import DistributionChart from "../../../components/charts/DistributionChart";

type DashboardStats = DashboardSummary;

const DashboardAdmin: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { user, getCsrfToken } = useAuth();

  // Estados para distribuições
  const [speciesData, setSpeciesData] = useState<DistributionResponse | null>(
    null
  );
  const [regionsData, setRegionsData] = useState<DistributionResponse | null>(
    null
  );
  const [citiesData, setCitiesData] = useState<DistributionResponse | null>(
    null
  );
  const [distributionLoading, setDistributionLoading] = useState(true);
  const [distributionError, setDistributionError] = useState("");

  // Estado para o período selecionado
  const [selectedPeriod, setSelectedPeriod] = useState<
    "7d" | "30d" | "90d" | "all"
  >("30d");
  const [customDates, setCustomDates] = useState({
    from: "",
    to: "",
  });
  const [useCustomDates, setUseCustomDates] = useState(false);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError("");

        const token = getCsrfToken();
        if (!token) {
          setError("Token de autenticação não encontrado");
          return;
        }

        // Carregar dados em paralelo
        const summaryData = await getDashboardSummary({ period: "30d" }, token);
        setStats(summaryData);
      } catch (err: any) {
        console.error("Erro ao carregar dados do dashboard:", err);

        if (err.response?.status === 401 || err.response?.status === 403) {
          setError(
            "Acesso negado. Verifique suas permissões de administrador."
          );
        } else if (err.response?.status === 400) {
          setError(
            `Erro nos parâmetros: ${
              err.response?.data?.message || "Parâmetros inválidos"
            }`
          );
        } else {
          setError("Erro ao carregar dados do dashboard. Tente novamente.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [getCsrfToken]);

  // Carregar dados de distribuição
  useEffect(() => {
    const loadDistributionData = async () => {
      try {
        setDistributionLoading(true);
        setDistributionError("");

        const token = getCsrfToken();
        if (!token) {
          setDistributionError("Token de autenticação não encontrado");
          return;
        }

        // Preparar parâmetros baseado no período selecionado
        const params =
          useCustomDates && customDates.from && customDates.to
            ? { from: customDates.from, to: customDates.to, limit: 5 }
            : selectedPeriod === "all"
            ? { limit: 5 } // Sem período = todos os dados
            : { period: selectedPeriod, limit: 5 };

        // Carregar dados de distribuição em paralelo
        const [species, regions, cities] = await Promise.all([
          getSpeciesDistribution(params, token),
          getRegionsDistribution(params, token),
          getCitiesDistribution(params, token),
        ]);

        setSpeciesData(species);
        setRegionsData(regions);
        setCitiesData(cities);
      } catch (err: any) {
        console.error("Erro ao carregar dados de distribuição:", err);
        setDistributionError(
          "Erro ao carregar gráficos de distribuição. Tente novamente."
        );
      } finally {
        setDistributionLoading(false);
      }
    };

    if (useCustomDates && (!customDates.from || !customDates.to || customDates.from > customDates.to)) {
      setDistributionLoading(false);
      setDistributionError("Informe um intervalo válido para consultar os gráficos.");
      return;
    }
    loadDistributionData();
  }, [getCsrfToken, selectedPeriod, customDates, useCustomDates]);

  return (
    <div className="account-profile-page account-dashboard">
      <Header />
      <div className="account-profile-layout">
        <div className="account-navigation"><Sidebar activeItem="dashboard" avatar={avatar} name={user?.nome_completo || "Administrador"} /></div>
        <main className="account-profile-content">
          <div className="account-page-heading"><h1>Visão geral</h1></div>
          {loading ? <div className="account-panel" role="status">Carregando indicadores…</div> : error ? <div className="alert alert-danger" role="alert">{error}</div> : stats ? (
            <div className="account-profile-sections">
              <div className="dashboard-metrics">
                {[
                  { label: "Usuários", value: stats.totalUsers, note: `+${stats.newUsersThisPeriod} nos últimos 30 dias` },
                  { label: "Imagens", value: stats.totalImages, note: `+${stats.newImagesThisPeriod} nos últimos 30 dias` },
                  { label: "Colaborações pendentes", value: stats.pendingCollaborations, note: "Aguardando aprovação" },
                  { label: "Imagens pendentes", value: stats.pendingImages, note: "Aguardando aprovação" },
                ].map((metric) => <section className="account-panel dashboard-metric" key={metric.label}>
                  <h2>{metric.label}</h2><strong>{metric.value.toLocaleString("pt-BR")}</strong><span>{metric.note}</span>
                </section>)}
              </div>
              <section className="account-panel" aria-labelledby="dashboard-users">
                <div className="account-panel-heading"><h2 id="dashboard-users">Usuários por perfil</h2></div>
                <div className="dashboard-user-types">
                  {[
                    { label: "Usuários comuns", count: stats.usersByType.common },
                    { label: "Colaboradores", count: stats.usersByType.collaborator },
                    { label: "Administradores", count: stats.usersByType.admin },
                  ].map((item) => {
                    const percentage = stats.totalUsers > 0 ? Math.min(100, Math.max(0, item.count / stats.totalUsers * 100)) : 0;
                    return <div key={item.label}>
                      <div className="dashboard-user-label"><span>{item.label}</span><strong>{item.count}</strong></div>
                      <div className="dashboard-user-track" role="progressbar" aria-label={item.label} aria-valuenow={percentage} aria-valuemin={0} aria-valuemax={100} aria-valuetext={`${item.count} usuários`}>
                        <div style={{ width: `${percentage}%` }} />
                      </div>
                    </div>;
                  })}
                </div>
              </section>
              <section className="account-panel" aria-labelledby="dashboard-distribution">
                <div className="account-panel-heading"><h2 id="dashboard-distribution">Distribuição de colaborações</h2></div>
                <div className="dashboard-filters">
                  <div><label className="form-label" htmlFor="distribution-period">Período dos gráficos</label>
                    <select id="distribution-period" className="form-select" value={useCustomDates ? "custom" : selectedPeriod} onChange={(event) => {
                      setUseCustomDates(event.target.value === "custom");
                      if (event.target.value !== "custom") setSelectedPeriod(event.target.value as typeof selectedPeriod);
                    }}>
                      <option value="7d">Últimos 7 dias</option><option value="30d">Últimos 30 dias</option>
                      <option value="90d">Últimos 90 dias</option><option value="all">Todo o período</option><option value="custom">Personalizado</option>
                    </select>
                  </div>
                  {useCustomDates && <>
                    <div><label className="form-label" htmlFor="distribution-from">Data inicial</label><input id="distribution-from" className="form-control" type="date" value={customDates.from} max={customDates.to || undefined} onChange={(event) => setCustomDates({ ...customDates, from: event.target.value })} /></div>
                    <div><label className="form-label" htmlFor="distribution-to">Data final</label><input id="distribution-to" className="form-control" type="date" value={customDates.to} min={customDates.from || undefined} onChange={(event) => setCustomDates({ ...customDates, to: event.target.value })} /></div>
                  </>}
                </div>
                {distributionError ? <div className="alert alert-danger" role="alert">{distributionError}</div> :
                  <div className="dashboard-charts">
                    <DistributionChart data={speciesData} title="Top 5 espécies" loading={distributionLoading} labelKey="species" />
                    <DistributionChart data={regionsData} title="Distribuição por região" loading={distributionLoading} labelKey="region" />
                    <DistributionChart data={citiesData} title="Top 5 municípios" loading={distributionLoading} labelKey="city" />
                  </div>}
              </section>
            </div>
          ) : <div className="account-panel">Dados não disponíveis.</div>}
        </main>
      </div>
    </div>
  );
};

export default DashboardAdmin;
